import type { TOC } from '@ember/component/template-only';
import { LinkTo } from '@ember/routing';
import { Heading, Text } from 'tio-ui/components/typography';
import type { ComponentLike } from '@glint/template';

/**
 * Color theme options for the quick action link
 */
export type QuickActionColor = 'violet' | 'amber' | 'teal' | 'emerald' | 'blue' | 'pink' | 'indigo';

/**
 * QuickActionLink - A styled navigation card for quick actions
 *
 * Used in dashboard grids to provide visual navigation to key features.
 * Includes icon, title, subtitle, and hover effects with configurable colors.
 *
 * @example
 * ```gts
 * <QuickActionLink
 *   @route="authenticated.reimagined.explore"
 *   @icon={{PhCompass}}
 *   @title="Explore Careers"
 *   @subtitle="150+ career paths"
 *   @color="violet"
 * />
 * ```
 */
interface QuickActionLinkSignature {
  Args: {
    /** Route to navigate to */
    route: string;
    /** Phosphor icon component */
    icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Title text */
    title: string;
    /** Optional subtitle text (hidden on small screens) */
    subtitle?: string;
    /** Color theme for the icon and hover border */
    color?: QuickActionColor;
    /** Additional CSS classes for the container */
    class?: string;
  };
  Element: HTMLAnchorElement;
}

const colorClasses: Record<
  QuickActionColor,
  { bg: string; text: string; hover: string; border: string }
> = {
  violet: {
    bg: 'bg-violet-100 dark:bg-violet-900/50',
    text: 'text-violet-600 dark:text-violet-400',
    hover: 'group-hover:bg-violet-600',
    border: 'hover:border-violet-300 dark:hover:border-violet-600',
  },
  amber: {
    bg: 'bg-amber-100 dark:bg-amber-900/50',
    text: 'text-amber-600 dark:text-amber-400',
    hover: 'group-hover:bg-amber-600',
    border: 'hover:border-amber-300 dark:hover:border-amber-600',
  },
  teal: {
    bg: 'bg-teal-100 dark:bg-teal-900/50',
    text: 'text-teal-600 dark:text-teal-400',
    hover: 'group-hover:bg-teal-600',
    border: 'hover:border-teal-300 dark:hover:border-teal-600',
  },
  emerald: {
    bg: 'bg-emerald-100 dark:bg-emerald-900/50',
    text: 'text-emerald-600 dark:text-emerald-400',
    hover: 'group-hover:bg-emerald-600',
    border: 'hover:border-emerald-300 dark:hover:border-emerald-600',
  },
  blue: {
    bg: 'bg-blue-100 dark:bg-blue-900/50',
    text: 'text-blue-600 dark:text-blue-400',
    hover: 'group-hover:bg-blue-600',
    border: 'hover:border-blue-300 dark:hover:border-blue-600',
  },
  pink: {
    bg: 'bg-pink-100 dark:bg-pink-900/50',
    text: 'text-pink-600 dark:text-pink-400',
    hover: 'group-hover:bg-pink-600',
    border: 'hover:border-pink-300 dark:hover:border-pink-600',
  },
  indigo: {
    bg: 'bg-indigo-100 dark:bg-indigo-900/50',
    text: 'text-indigo-600 dark:text-indigo-400',
    hover: 'group-hover:bg-indigo-600',
    border: 'hover:border-indigo-300 dark:hover:border-indigo-600',
  },
};

const getColorClasses = (color: QuickActionColor = 'violet') => colorClasses[color];

const QuickActionLink: TOC<QuickActionLinkSignature> = <template>
  {{#let (getColorClasses @color) as |colors|}}
    <LinkTo
      @route={{@route}}
      class="bg-white dark:bg-gray-900 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md
        {{colors.border}}
        transition-all group text-center
        {{@class}}"
      ...attributes
    >
      <div
        class="p-3
          {{colors.bg}}
          {{colors.text}}
          rounded-lg
          {{colors.hover}}
          group-hover:text-white transition-colors mx-auto w-fit mb-2"
      >
        {{#let @icon as |Icon|}}
          <Icon @size={{24}} />
        {{/let}}
      </div>
      <Heading
        @level={{3}}
        class="text-sm font-semibold text-gray-900 dark:text-white"
      >{{@title}}</Heading>
      {{#if @subtitle}}
        <Text
          @intent="muted"
          class="text-xs mt-1 hidden sm:block dark:text-gray-400"
        >{{@subtitle}}</Text>
      {{/if}}
    </LinkTo>
  {{/let}}
</template>;

export default QuickActionLink;
